/**
 * Model pricing table for cost_usd computation (#210).
 *
 * Prices are USD per 1M tokens. Unknown models fail open (return undefined).
 */

export interface ModelPricing {
  inputPerMillion: number;
  outputPerMillion: number;
}

/** Normalise model id for lookup (strip provider prefixes / version suffixes). */
export function normalizeModelId(model: string): string {
  return model.trim().toLowerCase();
}

const PRICING_TABLE: Record<string, ModelPricing> = {
  // Anthropic Claude
  "claude-opus-4-6": { inputPerMillion: 15, outputPerMillion: 75 },
  "claude-opus-4": { inputPerMillion: 15, outputPerMillion: 75 },
  "claude-sonnet-4": { inputPerMillion: 3, outputPerMillion: 15 },
  "claude-sonnet-4-6": { inputPerMillion: 3, outputPerMillion: 15 },
  "claude-haiku-4": { inputPerMillion: 0.8, outputPerMillion: 4 },
  // OpenAI GPT
  "gpt-5.5": { inputPerMillion: 2.5, outputPerMillion: 10 },
  "gpt-5.3-codex": { inputPerMillion: 2.5, outputPerMillion: 10 },
  "gpt-4.1": { inputPerMillion: 2, outputPerMillion: 8 },
  "gpt-4o": { inputPerMillion: 2.5, outputPerMillion: 10 },
  "gpt-4o-mini": { inputPerMillion: 0.15, outputPerMillion: 0.6 },
  // Google Gemini
  "gemini-2.5-pro": { inputPerMillion: 1.25, outputPerMillion: 10 },
  "gemini-2.5-flash": { inputPerMillion: 0.15, outputPerMillion: 0.6 },
  "gemini-2.0-flash": { inputPerMillion: 0.1, outputPerMillion: 0.4 },
};

export function resolveModelPricing(model: string | undefined): ModelPricing | undefined {
  if (!model) return undefined;
  const key = normalizeModelId(model);
  if (PRICING_TABLE[key]) return PRICING_TABLE[key];
  // Prefix match for versioned ids (e.g. claude-sonnet-4-20250514)
  for (const [known, pricing] of Object.entries(PRICING_TABLE)) {
    if (key.startsWith(known)) return pricing;
  }
  return undefined;
}

export function computeCostUsd(
  model: string | undefined,
  inputTokens: number,
  outputTokens: number,
): number | undefined {
  const pricing = resolveModelPricing(model);
  if (!pricing) return undefined;
  if (inputTokens <= 0 && outputTokens <= 0) return undefined;
  const cost =
    (inputTokens * pricing.inputPerMillion + outputTokens * pricing.outputPerMillion) /
    1_000_000;
  return Math.round(cost * 1_000_000) / 1_000_000;
}
